import { paymentTotal } from "../../lib/payments";
import { useEffect, useMemo, useState } from "react";
import { Plus, X, KeyRound, Pencil, Layers, Trash2, MessageCircle, ChevronDown, Camera, Upload, Edit2 } from "lucide-react";
import { toast } from "sonner";
import { api, inr, monthLabel, formatApiError, FEE_LABELS } from "../../lib/api";
import { STATUSES, POSITIONS } from "../../data/content";
import { useTaxonomy } from "../../lib/useTaxonomy";
import { ConfirmDeleteDialog } from "../../components/ConfirmDeleteDialog";
import { PackagePicker } from "../../components/PackagePicker";
import { packageByKey, fmtIso } from "../../lib/packages";

const inputCls = "w-full bg-[#0F0F0F] border border-white/10 text-white text-sm px-3 py-2.5 rounded-sm placeholder:text-white/30 focus:outline-none focus:border-[#D4AF37]";
const labelCls = "block text-[10px] uppercase tracking-widest text-white/40 mb-1.5";

const emptyPlayer = { name: "", dob: "", gender: "", guardian_name: "", guardian_phone: "", centre: "", batch: "", status: "Active", joining_date: "", leaving_date: "", source: "Manual", notes: "" };
export { emptyPlayer };

const planSummary = (plan) => {
  if (!plan) return <span className="text-white/30 text-xs">No plan</span>;
  const parts = [["group", plan.group_fee], ["transport", plan.transport_fee], ["pt", plan.pt_fee]].filter(([, v]) => v > 0);
  return (
    <span className="text-xs text-white/70">
      {plan.package && <span className="text-[#D4AF37] block mb-0.5" data-testid="plan-summary-package">{packageByKey(plan.package)?.label} package</span>}
      {parts.length ? parts.map(([t, v]) => `${plan.package && t === "group" ? "Package fee" : FEE_LABELS[t]} ${inr(v)}`).join(" · ") : "₹0"}
      <span className="text-[#D4AF37] block mt-0.5">{plan.package ? `renews ${fmtIso(plan.next_renewal)}` : `since ${monthLabel(plan.effective_from)}`}</span>
    </span>
  );
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PlayerPhoto = ({ player, size = 96, onChanged, editable = true }) => {
  const [bust, setBust] = useState(Date.now());
  const [uploading, setUploading] = useState(false);
  const hasPhoto = !!player.photo_url;

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) { toast.error("Image must be under 3 MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/players/${player.player_id}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo updated");
      setBust(Date.now());
      onChanged?.();
    } catch (err) { toast.error(formatApiError(err)); }
    setUploading(false);
  };

  const remove = async () => {
    if (!window.confirm("Remove this player's photo?")) return;
    try {
      await api.delete(`/admin/players/${player.player_id}/photo`);
      toast.success("Photo removed");
      onChanged?.();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="flex flex-col items-center gap-2" data-testid={`player-photo-${player.player_id}`}>
      <div className="relative shrink-0" style={{ width: size, height: size }}>
        {hasPhoto ? (
          <img
            src={`${BACKEND_URL}/api/players/${player.player_id}/photo?v=${bust}`}
            alt=""
            crossOrigin="use-credentials"
            className="w-full h-full object-cover rounded-full border-2 border-[#D4AF37]"
          />
        ) : (
          <div className="w-full h-full rounded-full border-2 border-dashed border-white/20 flex items-center justify-center text-white/30">
            <Camera size={size / 3} />
          </div>
        )}
      </div>
      {editable && (
        <div className="flex gap-2">
          <label className="cursor-pointer flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-[#D4AF37] border border-[#D4AF37]/40 px-3 py-1.5 hover:bg-[#D4AF37] hover:text-black transition-colors duration-200" data-testid="player-photo-upload">
            <Upload size={10} /> {uploading ? "Uploading..." : hasPhoto ? "Change" : "Add Photo"}
            <input type="file" accept="image/jpeg,image/png,image/webp" onChange={upload} className="hidden" disabled={uploading} />
          </label>
          {hasPhoto && (
            <button onClick={remove} className="text-red-400/70 hover:text-red-400 text-[10px] uppercase tracking-widest border border-red-400/30 px-3 py-1.5" data-testid="player-photo-remove">
              Remove
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export const PlayerForm = ({ initial, onSave, onClose, saveLabel, showPlan = false }) => {
  const [form, setForm] = useState(initial);
  const [plan, setPlan] = useState({ group_fee: "", transport_fee: "", pt_fee: "", registration_fee: "", effective_from: "", package: "", package_start: "" });
  const { centreNames, batchNames } = useTaxonomy();
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const setP = (k) => (e) => setPlan({ ...plan, [k]: e.target.value });
  const planHasAmount = ["group_fee", "transport_fee", "pt_fee", "registration_fee"].some((k) => plan[k] !== "" && Number(plan[k]) > 0);
  const derivedFrom = plan.package ? (plan.package_start || "").slice(0, 7) : (plan.start_date || form.joining_date || "").slice(0, 7);
  const submit = (e) => {
    e.preventDefault();
    if (!showPlan) { onSave(form, null); return; }
    if (planHasAmount && !derivedFrom) {
      toast.error(plan.package ? "Pick the package start date" : "Add a Joining Date or set the fee plan Start Date");
      return;
    }
    onSave(form, planHasAmount ? {
      group_fee: Number(plan.group_fee) || 0,
      transport_fee: Number(plan.transport_fee) || 0,
      pt_fee: Number(plan.pt_fee) || 0,
      registration_fee: Number(plan.registration_fee) || 0,
      effective_from: derivedFrom,
      package: plan.package || null,
      package_start: plan.package ? plan.package_start : null,
      start_date: plan.package ? plan.package_start : (plan.start_date || form.joining_date || null),
    } : null);
  };
  return (
    <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4" data-testid="player-form">
      <div><label className={labelCls}>Player Name *</label><input required value={form.name} onChange={set("name")} className={inputCls} data-testid="pf-name" /></div>
      <div><label className={labelCls}>Date of Birth</label><input type="date" min="1985-01-01" max={new Date().toISOString().slice(0, 10)} value={form.dob || ""} onChange={set("dob")} className={inputCls} data-testid="pf-dob" /></div>
      <div><label className={labelCls}>Gender</label>
        <select value={form.gender || ""} onChange={set("gender")} className={inputCls} data-testid="pf-gender">
          <option value="">—</option>
          <option value="Male">Male</option>
          <option value="Female">Female</option>
        </select></div>
      <div><label className={labelCls}>Guardian Name</label><input value={form.guardian_name || ""} onChange={set("guardian_name")} className={inputCls} data-testid="pf-guardian" /></div>
      <div><label className={labelCls}>WhatsApp Number</label><input value={form.guardian_phone || ""} onChange={set("guardian_phone")} className={inputCls} placeholder="98736 28804" data-testid="pf-phone" /></div>
      <div><label className={labelCls}>Centre</label>
        <select value={form.centre || ""} onChange={set("centre")} className={inputCls} data-testid="pf-centre">
          <option value="">—</option>
          {centreNames.map((c) => <option key={c} value={c}>{c}</option>)}
          {form.centre && !centreNames.includes(form.centre) && <option value={form.centre}>{form.centre}</option>}
        </select></div>
      <div><label className={labelCls}>Batch</label>
        <select value={form.batch || ""} onChange={set("batch")} className={inputCls} data-testid="pf-batch">
          <option value="">—</option>
          {batchNames.map((b) => <option key={b} value={b}>{b}</option>)}
          {form.batch && !batchNames.includes(form.batch) && <option value={form.batch}>{form.batch}</option>}
        </select></div>
      <div><label className={labelCls}>Status</label>
        <select value={form.status} onChange={set("status")} className={inputCls} data-testid="pf-status">
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select></div>
      <div><label className={labelCls}>Position</label>
        <select value={form.position || ""} onChange={set("position")} className={inputCls} data-testid="pf-position">
          <option value="">—</option>
          {POSITIONS.map((p) => <option key={p} value={p}>{p}</option>)}
        </select></div>
      <div><label className={labelCls}>Strong Foot</label>
        <select value={form.strong_foot || ""} onChange={set("strong_foot")} className={inputCls} data-testid="pf-foot">
          <option value="">—</option>
          {["Right", "Left", "Both"].map((f) => <option key={f} value={f}>{f}</option>)}
        </select></div>
      <div><label className={labelCls}>Joining Date</label><input type="date" value={form.joining_date || ""} onChange={set("joining_date")} className={inputCls} data-testid="pf-joining" /></div>
      <div><label className={labelCls}>Leaving Date</label><input type="date" value={form.leaving_date || ""} onChange={set("leaving_date")} className={inputCls} data-testid="pf-leaving" /></div>
      <div><label className={labelCls}>Source</label>
        <select value={form.source || "Manual"} onChange={set("source")} className={inputCls} data-testid="pf-source">
          {["Manual", "Form", "Existing Database"].map((s) => <option key={s} value={s}>{s}</option>)}
        </select></div>
      <div className="sm:col-span-2"><label className={labelCls}>Notes</label><input value={form.notes || ""} onChange={set("notes")} className={inputCls} data-testid="pf-notes" /></div>

      {showPlan && (
        <div className="sm:col-span-2 border-t border-[#D4AF37]/25 pt-5 mt-2" data-testid="pf-plan-section">
          <p className="text-[10px] tracking-[0.28em] uppercase text-[#D4AF37] font-semibold mb-1">Initial Fee Plan <span className="text-white/40">(optional)</span></p>
          <p className="text-white/40 text-xs mb-4">Leave everything blank if you'll set the plan later. Fill any amount below to create the plan alongside this player.</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <PackagePicker plan={plan} setPlan={setPlan} testid="pf-pkg" />
            <div><label className={labelCls}>{plan.package ? "Payable Package Fee ₹" : "Group ₹"}</label><input type="number" min="0" placeholder="0" value={plan.group_fee} onChange={setP("group_fee")} className={inputCls} data-testid="pf-plan-group" /></div>
            <div><label className={labelCls}>{plan.package ? "Transport for package period ₹" : "Transport ₹"}</label><input type="number" min="0" placeholder="0" value={plan.transport_fee} onChange={setP("transport_fee")} className={inputCls} data-testid="pf-plan-transport" /></div>
            <div><label className={labelCls}>PT ₹</label><input type="number" min="0" placeholder="0" value={plan.pt_fee} onChange={setP("pt_fee")} className={inputCls} data-testid="pf-plan-pt" /></div>
            <div><label className={labelCls}>Registration ₹ <span className="text-white/30 normal-case">(one-time)</span></label><input type="number" min="0" placeholder="0" value={plan.registration_fee} onChange={setP("registration_fee")} className={inputCls} data-testid="pf-plan-registration" /></div>
            {!plan.package && <div className="col-span-2 sm:col-span-1">
              <label className={labelCls}>Start Date {planHasAmount && !derivedFrom && <span className="text-red-400">*</span>}</label>
              <input type="date" value={plan.start_date || ""} onChange={setP("start_date")} className={inputCls} data-testid="pf-plan-from" />
              {!plan.start_date && form.joining_date && (
                <p className="text-white/40 text-[10px] mt-1">Defaults to joining date ({form.joining_date})</p>
              )}
            </div>}
          </div>
        </div>
      )}

      <div className="sm:col-span-2 flex gap-3 pt-2">
        <button type="submit" className="bg-[#D4AF37] text-black font-bold uppercase tracking-widest text-xs px-8 py-3 rounded-sm hover:bg-[#F3D059] transition-colors duration-200" data-testid="pf-save">{saveLabel}</button>
        <button type="button" onClick={onClose} className="border border-white/20 text-white/70 text-xs uppercase tracking-widest px-6 py-3 rounded-sm hover:border-[#D4AF37]" data-testid="pf-cancel">Cancel</button>
      </div>
    </form>
  );
};

const slugName = (name) => (name || "").toLowerCase().replace(/[^a-z0-9]+/g, ".").replace(/^\.|\.$/g, "") || "player";
const defaultParentEmail = (name) => `${slugName(name)}@zavifc`;
const defaultParentPassword = (phone, playerId) => {
  const digits = (phone || "").replace(/\D/g, "");
  return digits.length >= 8 ? digits : (playerId || "changeme12");
};

const WelcomeModal = ({ player, onClose }) => {
  const [form, setForm] = useState({
    email: defaultParentEmail(player.name),
    password: defaultParentPassword(player.guardian_phone, player.player_id),
  });
  const [result, setResult] = useState(null);
  const [sending, setSending] = useState(false);

  const send = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post(`/admin/players/${player.player_id}/welcome-message`, form);
      setResult(data);
      toast.success("Welcome message ready");
    } catch (err) { toast.error(formatApiError(err)); }
    setSending(false);
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-start justify-center px-4 overflow-y-auto py-10" onClick={onClose} data-testid="welcome-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-[#0A0A0A] border border-white/10 p-6 sm:p-8 w-full max-w-lg">
        <div className="flex justify-between items-start mb-4">
          <div>
            <p className="text-xs tracking-[0.25em] uppercase text-[#D4AF37] font-semibold">Welcome WhatsApp</p>
            <h2 className="font-heading text-lg font-black mt-1">{player.name}</h2>
          </div>
          <button onClick={onClose} className="text-white/50 hover:text-white"><X size={20} /></button>
        </div>
        {!result ? (
          <form onSubmit={send} className="space-y-3">
            <p className="text-white/50 text-xs mb-1">Set/reset the parent's portal credentials — they'll be included in the welcome message.</p>
            <input required type="email" placeholder="Parent email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} data-testid="welcome-email" />
            <input required minLength={8} placeholder="Password (min 8)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputCls} data-testid="welcome-password" />
            <button type="submit" disabled={sending} className="w-full bg-[#D4AF37] text-black font-bold uppercase tracking-widest text-xs px-6 py-3 rounded-sm hover:bg-[#F3D059] transition-colors duration-200 disabled:opacity-50" data-testid="welcome-generate">
              {sending ? "Preparing..." : "Generate Welcome Message"}
            </button>
          </form>
        ) : (
          <div>
            <pre className="text-white/70 text-xs bg-[#050505] border border-white/10 p-4 whitespace-pre-wrap font-sans mb-4 max-h-72 overflow-y-auto" data-testid="welcome-message-preview">{result.message}</pre>
            {result.wa_link ? (
              <a href={result.wa_link} target="_blank" rel="noreferrer" className="w-full flex items-center justify-center gap-2 bg-[#D4AF37] text-black font-bold uppercase tracking-widest text-xs px-6 py-3 rounded-sm hover:bg-[#F3D059] transition-colors duration-200" data-testid="welcome-send-wa">
                <MessageCircle size={14} /> Open in WhatsApp
              </a>
            ) : (
              <p className="text-orange-300 text-xs border border-orange-400/40 p-3">No WhatsApp number on file. Add a guardian phone first.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const PlayerDetail = ({ playerId, onClose }) => {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [plan, setPlan] = useState({ group_fee: "", transport_fee: "", pt_fee: "", opening_dues: "", registration_fee: "", effective_from: "", package: "", package_start: "" });
  const [editingPlanId, setEditingPlanId] = useState(null);
  const [login, setLogin] = useState({ email: "", password: "" });
  const [ov, setOv] = useState({ month: "", group: "", transport: "", pt: "", previous_dues: "", reason: "" });
  const [confirmDelete, setConfirmDelete] = useState(false);

  const load = () => api.get(`/admin/players/${playerId}`).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, [playerId]);

  if (!data) return null;
  const { player, fee_plans, payments, pauses, overrides, login: loginInfo } = data;

  const doDelete = async () => {
    try {
      await api.delete(`/admin/players/${playerId}`);
      toast.success(`${player.name} deleted`);
      setConfirmDelete(false);
      onClose();
    } catch (err) { toast.error(formatApiError(err)); throw err; }
  };

  const saveEdit = async (form) => {
    try {
      await api.put(`/admin/players/${playerId}`, { ...form, dob: form.dob || null, joining_date: form.joining_date || null, leaving_date: form.leaving_date || null });
      toast.success("Player updated");
      setEditing(false);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const savePlan = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        group_fee: Number(plan.group_fee) || 0, transport_fee: Number(plan.transport_fee) || 0,
        pt_fee: Number(plan.pt_fee) || 0, opening_dues: Number(plan.opening_dues) || 0,
        registration_fee: Number(plan.registration_fee) || 0,
        effective_from: plan.package ? plan.package_start.slice(0, 7) : plan.start_date.slice(0, 7),
        start_date: plan.package ? plan.package_start : plan.start_date,
        package: plan.package || null,
        package_start: plan.package ? plan.package_start : null,
      };
      if (editingPlanId) {
        // In-place update — supports changing Effective From on the ongoing plan.
        // Dues for newly covered historical months are automatically computed
        // (no duplicate rows created). Overrides and payments stay intact.
        await api.put(`/admin/players/${playerId}/fee-plans/${editingPlanId}`, payload);
      } else {
        await api.post(`/admin/players/${playerId}/fee-plans`, payload);
      }
      toast.success(editingPlanId ? "Fee plan updated" : "Fee plan saved");
      setPlan({ group_fee: "", transport_fee: "", pt_fee: "", opening_dues: "", registration_fee: "", effective_from: "", package: "", package_start: "" });
      setEditingPlanId(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const saveOverride = async (e) => {
    e.preventDefault();
    const charges = {};
    for (const t of ["group", "transport", "pt", "previous_dues"]) if (ov[t] !== "") charges[t] = Number(ov[t]);
    if (!ov.month || Object.keys(charges).length === 0) { toast.error("Pick a month and at least one amount"); return; }
    try {
      await api.post("/admin/overrides", { player_id: playerId, month: ov.month, charges, reason: ov.reason });
      toast.success("Month adjusted");
      setOv({ month: "", group: "", transport: "", pt: "", previous_dues: "", reason: "" });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const removeOverride = async (id) => {
    try {
      await api.delete(`/admin/overrides/${id}`);
      toast.success("Override removed");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const saveLogin = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/admin/players/${playerId}/login`, login);
      toast.success(`Login ready: ${login.email} / ${login.password} — share with the parent`);
      setLogin({ email: "", password: "" });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const removePlayer = () => setConfirmDelete(true);

  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm overflow-y-auto" onClick={onClose} data-testid="player-detail-modal">
      <div className="w-full min-h-screen sm:max-w-3xl sm:mx-auto sm:my-10 sm:min-h-0 bg-[#0A0A0A] border border-white/10 p-5 sm:p-10 relative" onClick={(e) => e.stopPropagation()}>
        {/* Close — persistent top-right */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 w-11 h-11 flex items-center justify-center text-white/60 hover:text-white bg-black/40 rounded-sm z-10"
          aria-label="Close"
          data-testid="detail-close">
          <X size={22} />
        </button>

        {/* --- MOBILE HEADER (single column, centred) --- */}
        <div className="md:hidden flex flex-col items-center text-center pt-6 pb-6 border-b border-white/8 mb-6">
          <PlayerPhoto player={player} size={112} onChanged={load} />
          <h2 className="font-heading text-2xl font-black mt-4" data-testid="detail-player-name">{player.name}</h2>
          <p className="text-xs tracking-[0.25em] uppercase text-[#D4AF37] font-semibold mt-1">{player.player_id}</p>
          <div className="mt-3 flex flex-wrap gap-2 justify-center">
            <span className={`text-[10px] uppercase tracking-widest px-2.5 py-1 border ${player.status === "Active" ? "bg-[#D4AF37]/15 text-[#D4AF37] border-[#D4AF37]/30" : "bg-white/5 text-white/60 border-white/15"}`} data-testid="detail-status-chip">{player.status}</span>
            {player.gender && <span className="text-[10px] uppercase tracking-widest px-2.5 py-1 border border-white/15 text-white/70">{player.gender}</span>}
            {player.strong_foot && <span className="text-[10px] uppercase tracking-widest px-2.5 py-1 border border-white/15 text-white/70">{player.strong_foot} foot</span>}
          </div>
        </div>

        {/* Mobile: label/value pairs */}
        <div className="md:hidden space-y-3 mb-6">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-white/40 font-semibold">Centre</p>
            <p className="text-white text-sm mt-0.5">{player.centre || "—"}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-widest text-white/40 font-semibold">Batch</p>
            <p className="text-white text-sm mt-0.5">{player.batch || "—"}</p>
          </div>
          {player.dob && (
            <div>
              <p className="text-[10px] uppercase tracking-widest text-white/40 font-semibold">Date of Birth</p>
              <p className="text-white text-sm mt-0.5">{player.dob}</p>
            </div>
          )}
          {player.joining_date && (
            <div>
              <p className="text-[10px] uppercase tracking-widest text-white/40 font-semibold">Joining Date</p>
              <p className="text-white text-sm mt-0.5">{player.joining_date}</p>
            </div>
          )}
          {player.guardian_name && (
            <div>
              <p className="text-[10px] uppercase tracking-widest text-white/40 font-semibold">Guardian</p>
              <p className="text-white text-sm mt-0.5">{player.guardian_name}{player.guardian_phone ? ` · ${player.guardian_phone}` : ""}</p>
            </div>
          )}
        </div>

        {/* Mobile primary action */}
        {!editing && (
          <button onClick={() => setEditing(true)} className="md:hidden w-full flex items-center justify-center gap-2 bg-[#D4AF37] text-black text-xs font-bold uppercase tracking-widest px-5 py-3.5 rounded-sm hover:bg-[#F3D059] mb-8" data-testid="detail-edit-mobile">
            <Pencil size={13} /> Edit Details
          </button>
        )}

        {/* --- DESKTOP HEADER (unchanged shape) --- */}
        <div className="hidden md:flex items-start justify-between gap-4 mb-8">
          <div className="flex items-start gap-5 min-w-0">
            <PlayerPhoto player={player} size={96} onChanged={load} />
            <div className="min-w-0">
              <p className="text-xs tracking-[0.25em] uppercase text-[#D4AF37] font-semibold">{player.player_id}</p>
              <h2 className="font-heading text-2xl font-black mt-1">{player.name}</h2>
              <p className="text-white/50 text-sm mt-1">{player.batch || "No batch"} · {player.centre || "No centre"} · {player.status}</p>
              {(player.gender || player.position || player.strong_foot) && <p className="text-white/50 text-sm mt-1">{[player.gender, player.position, player.strong_foot && `${player.strong_foot} foot`].filter(Boolean).join(" · ")}</p>}
              {player.guardian_name && <p className="text-white/50 text-sm mt-1">Guardian: {player.guardian_name} {player.guardian_phone ? `· ${player.guardian_phone}` : ""}</p>}
            </div>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            {!editing && (
              <button onClick={() => setEditing(true)} className="flex items-center gap-2 border border-[#D4AF37] text-[#D4AF37] text-xs font-bold uppercase tracking-widest px-4 py-2 rounded-sm hover:bg-[#D4AF37] hover:text-black transition-colors duration-200" data-testid="detail-edit">
                <Pencil size={13} /> Edit Details
              </button>
            )}
          </div>
        </div>

        <ConfirmDeleteDialog
          open={confirmDelete}
          onClose={() => setConfirmDelete(false)}
          onConfirm={doDelete}
          title="Delete Player"
          itemLabel={`${player.name} · ${player.player_id}`}
          firstMessage={`You're about to permanently delete this player. This will also remove their parent login, fee plans, payments, attendance records, overrides, pauses, skill reports, photo, and reminder history. This cannot be undone.`}
          secondMessage={`Final confirmation — this deletes everything tied to ${player.name}. Type DELETE below to enable the button.`}
          confirmWord="DELETE"
          testid="confirm-player-delete"
        />

        {editing && (
          <div className="border border-[#D4AF37]/40 p-6 mb-10" data-testid="detail-edit-panel">
            <PlayerForm initial={{ ...emptyPlayer, ...player }} onSave={saveEdit} onClose={() => setEditing(false)} saveLabel="Save Changes" />
          </div>
        )}

        <h3 className="text-xs tracking-[0.25em] uppercase text-white/40 font-semibold mb-4">Fee Plan History</h3>
        {fee_plans.length === 0 && <p className="text-white/40 text-sm mb-4">No fee plan yet.</p>}
        {fee_plans.map((p) => {
          const isOngoing = !p.effective_to;
          return (
            <div key={p.id} className={`flex flex-wrap gap-x-6 gap-y-1 text-sm border p-4 mb-2 ${editingPlanId === p.id ? "border-[#D4AF37] bg-[#D4AF37]/5" : "border-white/10"}`} data-testid={`plan-${p.id}`}>
              <span className="text-[#D4AF37] font-semibold flex-1 min-w-[200px]">
                {p.start_date && !p.package ? fmtIso(p.start_date) : monthLabel(p.effective_from)} → {p.effective_to ? monthLabel(p.effective_to) : "ongoing"}
              </span>
              {p.package && <span className="text-[#D4AF37] w-full text-xs uppercase tracking-widest" data-testid={`plan-package-${p.id}`}>{packageByKey(p.package)?.label} package · started {fmtIso(p.package_start)}{isOngoing && p.next_renewal ? ` · renews ${fmtIso(p.next_renewal)}` : ""}</span>}
              <span className="text-white/70">{p.package ? "Package fee" : "Group"} {inr(p.group_fee)}</span>
              <span className="text-white/70">Transport {inr(p.transport_fee)}</span>
              <span className="text-white/70">PT {inr(p.pt_fee)}</span>
              {p.opening_dues > 0 && <span className="text-white/70">Opening dues {inr(p.opening_dues)}</span>}
              {p.registration_fee > 0 && <span className="text-[#D4AF37]">Registration {inr(p.registration_fee)}</span>}
              {isOngoing && (
                <button
                  type="button"
                  onClick={() => {
                    setEditingPlanId(p.id);
                    setPlan({
                      group_fee: p.group_fee ?? "",
                      transport_fee: p.transport_fee ?? "",
                      pt_fee: p.pt_fee ?? "",
                      opening_dues: p.opening_dues ?? "",
                      registration_fee: p.registration_fee ?? "",
                      effective_from: p.effective_from,
                      start_date: p.start_date || `${p.effective_from}-01`,
                      package: p.package || "",
                      package_start: p.package_start || "",
                    });
                    document.querySelector('[data-testid="fee-plan-form"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
                  }}
                  className="text-[#D4AF37]/80 hover:text-[#D4AF37] inline-flex items-center gap-1 text-xs uppercase tracking-widest"
                  data-testid={`plan-edit-${p.id}`}
                >
                  <Edit2 size={13} /> Edit
                </button>
              )}
              {p.notes && <span className="text-white/40 text-xs w-full">{p.notes}</span>}
            </div>
          );
        })}
        <p className="text-white/40 text-xs uppercase tracking-[0.24em] mb-2 mt-4" data-testid="fee-plan-form-title">
          {editingPlanId ? "Update ongoing plan" : "Add new fee plan"}
        </p>
        <form onSubmit={savePlan} className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-2 mb-10" data-testid="fee-plan-form">
          <PackagePicker plan={plan} setPlan={setPlan} testid="plan-pkg" baseline={fee_plans.find((x) => !x.effective_to)} />
          {plan.package && <p className="col-span-2 sm:col-span-3 text-white/40 text-[11px]" data-testid="plan-pkg-hint">Earlier months, payments and balances stay unchanged. The previous plan ends the month before the package start, and any payment already made in the start month counts towards the package.</p>}
          <div><label className={labelCls}>{plan.package ? "Payable Package Fee ₹ *" : "Group ₹ *"}</label><input required type="number" min="0" placeholder="0" value={plan.group_fee} onChange={(e) => setPlan({ ...plan, group_fee: e.target.value })} className={inputCls} data-testid="plan-group" /></div>
          <div><label className={labelCls}>{plan.package ? "Transport for package period ₹" : "Transport ₹"}</label><input type="number" min="0" placeholder="0" value={plan.transport_fee} onChange={(e) => setPlan({ ...plan, transport_fee: e.target.value })} className={inputCls} data-testid="plan-transport" /></div>
          <div><label className={labelCls}>PT ₹</label><input type="number" min="0" placeholder="0" value={plan.pt_fee} onChange={(e) => setPlan({ ...plan, pt_fee: e.target.value })} className={inputCls} data-testid="plan-pt" /></div>
          <div><label className={labelCls}>Opening Dues ₹</label><input type="number" min="0" placeholder="0" value={plan.opening_dues} onChange={(e) => setPlan({ ...plan, opening_dues: e.target.value })} className={inputCls} data-testid="plan-opening" /></div>
          <div><label className={labelCls}>Registration Fee ₹ <span className="text-white/30 normal-case">(set 0 to cancel)</span></label><input type="number" min="0" placeholder="0" value={plan.registration_fee} onChange={(e) => setPlan({ ...plan, registration_fee: e.target.value })} className={inputCls} data-testid="plan-registration" /></div>
          {!plan.package && <div><label className={labelCls}>Start Date *{editingPlanId && <span className="text-white/30 normal-case ml-1">(editable — backdate to cover historical months)</span>}</label><input required type="date" value={plan.start_date || ""} onChange={(e) => setPlan({ ...plan, start_date: e.target.value })} className={inputCls} data-testid="plan-from" /><p className="text-white/30 text-[10px] mt-1">Billed from the 1st of this month (full month).</p></div>}
          <button type="submit" className={`col-span-2 ${editingPlanId ? "sm:col-span-2" : "sm:col-span-3"} bg-[#D4AF37] text-black font-bold uppercase tracking-widest text-xs px-6 py-3 rounded-sm hover:bg-[#F3D059] transition-colors duration-200`} data-testid="plan-save">
            {editingPlanId ? "Update Ongoing Plan" : "Add Fee Plan"}
          </button>
          {editingPlanId && (
            <button
              type="button"
              onClick={() => {
                setEditingPlanId(null);
                setPlan({ group_fee: "", transport_fee: "", pt_fee: "", opening_dues: "", registration_fee: "", effective_from: "", package: "", package_start: "" });
              }}
              className="col-span-2 sm:col-span-1 border border-white/15 text-white/70 font-bold uppercase tracking-widest text-xs px-6 py-3 rounded-sm hover:border-white/40 hover:text-white transition-colors duration-200"
              data-testid="plan-cancel-edit"
            >
              Cancel
            </button>
          )}
        </form>

        <h3 className="text-xs tracking-[0.25em] uppercase text-white/40 font-semibold mb-4">Monthly Adjustments</h3>
        {overrides.length === 0 && <p className="text-white/40 text-sm mb-4">No month-specific adjustments.</p>}
        {overrides.map((o) => (
          <div key={o.id} className="flex items-center justify-between text-sm border border-white/10 p-4 mb-2" data-testid={`override-${o.id}`}>
            <span className="text-white/70">
              <span className="text-[#D4AF37] font-semibold">{monthLabel(o.month)}</span> — {Object.entries(o.charges).map(([t, v]) => `${FEE_LABELS[t]} ${inr(v)}`).join(", ")}
              {o.reason && <span className="text-white/40 text-xs ml-2">({o.reason})</span>}
            </span>
            <button onClick={() => removeOverride(o.id)} className="text-red-400/70 hover:text-red-400" data-testid={`override-del-${o.id}`}><Trash2 size={15} /></button>
          </div>
        ))}
        <form onSubmit={saveOverride} className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-4 mb-10" data-testid="override-form">
          <input required type="month" value={ov.month} onChange={(e) => setOv({ ...ov, month: e.target.value })} className={inputCls} data-testid="ov-month" />
          {["group", "transport", "pt", "previous_dues"].map((t) => (
            <input key={t} type="number" min="0" placeholder={`${FEE_LABELS[t]} ₹`} value={ov[t]} onChange={(e) => setOv({ ...ov, [t]: e.target.value })} className={inputCls} data-testid={`detail-ov-${t}`} />
          ))}
          <input placeholder="Reason (optional)" value={ov.reason} onChange={(e) => setOv({ ...ov, reason: e.target.value })} className={inputCls} data-testid="ov-reason" />
          <button type="submit" className="col-span-2 sm:col-span-3 border border-[#D4AF37] text-[#D4AF37] font-bold uppercase tracking-widest text-xs px-6 py-3 rounded-sm hover:bg-[#D4AF37] hover:text-black transition-colors duration-200" data-testid="ov-save">Adjust That Month Only</button>
        </form>

        <h3 className="text-xs tracking-[0.25em] uppercase text-white/40 font-semibold mb-4 flex items-center gap-2"><KeyRound size={14} className="text-[#D4AF37]" /> Parent Login</h3>
        {loginInfo && <p className="text-sm text-white/70 mb-3" data-testid="detail-login-email">Active login: <span className="text-[#D4AF37]">{loginInfo.email}</span></p>}
        <form onSubmit={saveLogin} className="grid sm:grid-cols-3 gap-3 mb-10" data-testid="login-create-form">
          <input required type="email" placeholder="Parent email" value={login.email} onChange={(e) => setLogin({ ...login, email: e.target.value })} className={inputCls} data-testid="login-email-input" />
          <input required type="text" minLength={8} placeholder="Password (min 8)" value={login.password} onChange={(e) => setLogin({ ...login, password: e.target.value })} className={inputCls} data-testid="login-password-input" />
          <button type="submit" className="border border-[#D4AF37] text-[#D4AF37] font-bold uppercase tracking-widest text-xs px-6 py-3 rounded-sm hover:bg-[#D4AF37] hover:text-black transition-colors duration-200" data-testid="login-save">
            {loginInfo ? "Reset Password" : "Create Login"}
          </button>
        </form>

        <h3 className="text-xs tracking-[0.25em] uppercase text-white/40 font-semibold mb-4">Recent Payments</h3>
        {payments.length === 0 && <p className="text-white/40 text-sm mb-8">No payments recorded.</p>}
        {payments.slice(0, 5).map((p) => (
          <div key={p.id} className="flex justify-between text-sm border-b border-white/5 py-2" data-testid={`payment-${p.id}`}>
            <span className="text-white/70">{p.date} · {p.mode}</span>
            <span className="text-[#D4AF37] font-semibold">{inr(paymentTotal(p))}</span>
          </div>
        ))}

        {pauses.length > 0 && (
          <>
            <h3 className="text-xs tracking-[0.25em] uppercase text-white/40 font-semibold mt-8 mb-4">Billing Holds</h3>
            {pauses.map((p) => (
              <p key={p.id} className="text-sm text-white/60" data-testid={`pause-${p.id}`}>{p.month} — {p.reason}</p>
            ))}
          </>
        )}

        {/* --- DANGER ZONE (bottom) --- */}
        {!editing && (
          <div className="mt-14 pt-6 border-t border-red-500/20" data-testid="danger-zone">
            <p className="text-[10px] tracking-[0.28em] uppercase text-red-400/80 font-bold">Danger Zone</p>
            <p className="text-white/50 text-xs mt-1.5 max-w-md">Deleting a player removes their parent login, fee plans, payments, attendance records, overrides, pauses, skill reports, photo and reminder history. This cannot be undone.</p>
            <button onClick={removePlayer} className="mt-4 inline-flex items-center gap-2 border border-red-400/60 text-red-400 text-xs font-bold uppercase tracking-widest px-4 py-2.5 rounded-sm hover:bg-red-500 hover:text-white transition-colors duration-200" data-testid="detail-delete">
              <Trash2 size={13} /> Delete Player
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default function Players() {
  const [players, setPlayers] = useState([]);
  const [search, setSearch] = useState("");
  const [centreFilter, setCentreFilter] = useState("");
  const [batchFilter, setBatchFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("Active");
  const [yobFilter, setYobFilter] = useState(new Set());
  const [yobOpen, setYobOpen] = useState(false);
  const [bulkMode, setBulkMode] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulk, setBulk] = useState({ group_fee: "", transport_fee: "", pt_fee: "", effective_from: "" });
  const [showAdd, setShowAdd] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [welcomePlayer, setWelcomePlayer] = useState(null);
  const { centreNames, batchNames } = useTaxonomy();

  const [playersLoaded, setPlayersLoaded] = useState(false);
  const load = () => api.get("/admin/players")
    .then((r) => { setPlayers(r.data); setPlayersLoaded(true); })
    .catch(() => setPlayersLoaded(true));
  useEffect(() => { load(); }, []);

  const yobOptions = useMemo(() => {
    const years = new Set();
    players.forEach((p) => { if (p.dob) years.add(p.dob.slice(0, 4)); });
    return [...years].sort().reverse();
  }, [players]);

  const addPlayer = async (form, plan) => {
    try {
      const { data: created } = await api.post("/admin/players", { ...form, dob: form.dob || null, joining_date: form.joining_date || null, leaving_date: form.leaving_date || null });
      if (plan) {
        try {
          await api.post(`/admin/players/${created.player_id}/fee-plans`, plan);
          toast.success(`Player + fee plan saved (${created.player_id})`);
        } catch (err) {
          toast.error(`Player saved but plan failed: ${formatApiError(err)}`);
        }
      } else {
        toast.success(`Player added (${created.player_id})`);
      }
      setShowAdd(false);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const applyBulk = async (e) => {
    e.preventDefault();
    if (selected.size === 0) { toast.error("Select at least one player"); return; }
    try {
      const body = {
        player_ids: [...selected],
        effective_from: bulk.effective_from,
        group_fee: bulk.group_fee !== "" ? Number(bulk.group_fee) : null,
        transport_fee: bulk.transport_fee !== "" ? Number(bulk.transport_fee) : null,
        pt_fee: bulk.pt_fee !== "" ? Number(bulk.pt_fee) : null,
      };
      const { data } = await api.post("/admin/fee-plans/bulk", body);
      toast.success(`Updated ${data.updated} players${data.skipped.length ? ` · skipped ${data.skipped.length} (plan starts later)` : ""}`);
      setSelected(new Set());
      setBulkMode(false);
      setBulk({ group_fee: "", transport_fee: "", pt_fee: "", effective_from: "" });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const filtered = players.filter((p) =>
    (!centreFilter || p.centre === centreFilter) &&
    (!batchFilter || p.batch === batchFilter) &&
    (statusFilter === "all" || (p.status || "Active") === statusFilter) &&
    (yobFilter.size === 0 || (p.dob && yobFilter.has(p.dob.slice(0, 4)))) &&
    `${p.player_id} ${p.name} ${p.batch || ""}`.toLowerCase().includes(search.toLowerCase()));

  const toggleAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map((p) => p.player_id)));
  };

  const toggle = (pid) => {
    const next = new Set(selected);
    next.has(pid) ? next.delete(pid) : next.add(pid);
    setSelected(next);
  };

  const toggleYob = (y) => {
    const next = new Set(yobFilter);
    next.has(y) ? next.delete(y) : next.add(y);
    setYobFilter(next);
  };

  return (
    <div data-testid="admin-players">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <h1 className="font-heading text-3xl font-black uppercase tracking-tight" data-testid="players-heading">Players <span className="text-[#D4AF37]">{playersLoaded ? `(${filtered.length})` : "…"}</span></h1>
        <div className="flex gap-3 flex-wrap">
          <button onClick={() => { setBulkMode(!bulkMode); setSelected(new Set()); }}
            className={`flex items-center gap-2 font-bold uppercase tracking-widest text-xs px-5 py-2.5 rounded-sm border transition-colors duration-200 ${bulkMode ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black"}`}
            data-testid="bulk-toggle">
            <Layers size={15} /> Bulk Fee Update
          </button>
          <button onClick={() => setShowAdd(true)} className="flex items-center gap-2 bg-[#D4AF37] text-black font-bold uppercase tracking-widest text-xs px-5 py-2.5 rounded-sm hover:bg-[#F3D059] transition-colors duration-200" data-testid="players-add">
            <Plus size={15} /> Add Player
          </button>
        </div>
      </div>

      <div className="flex gap-3 flex-wrap mb-6 items-start">
        <input placeholder="Search name / ID / batch" value={search} onChange={(e) => setSearch(e.target.value)} className={inputCls + " w-64"} data-testid="players-search" />
        <select value={centreFilter} onChange={(e) => setCentreFilter(e.target.value)} className={inputCls + " w-auto"} data-testid="filter-centre">
          <option value="">All Centres</option>
          {centreNames.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={batchFilter} onChange={(e) => setBatchFilter(e.target.value)} className={inputCls + " w-auto"} data-testid="filter-batch">
          <option value="">All Batches</option>
          {batchNames.map((b) => <option key={b} value={b}>{b}</option>)}
        </select>
        <div className="flex border border-white/10 rounded-sm overflow-hidden" data-testid="filter-status">
          {[["Active", "Active"], ["Inactive", "Inactive"], ["all", "All"]].map(([v, label]) => (
            <button key={v} onClick={() => setStatusFilter(v)}
              className={`text-[11px] uppercase tracking-widest px-3 py-2.5 transition-colors duration-200 ${statusFilter === v ? "bg-[#D4AF37] text-black font-bold" : "text-white/60 hover:text-white"}`}
              data-testid={`filter-status-${v.toLowerCase()}`}>
              {label}
            </button>
          ))}
        </div>
        <div className="relative">
          <button onClick={() => setYobOpen(!yobOpen)} className={inputCls + " flex items-center gap-2 min-w-[180px] justify-between"} data-testid="filter-yob-toggle">
            <span className="text-white/70">{yobFilter.size === 0 ? "All Years of Birth" : `${yobFilter.size} year${yobFilter.size === 1 ? "" : "s"} selected`}</span>
            <ChevronDown size={14} className="text-white/40" />
          </button>
          {yobOpen && (
            <div className="absolute z-20 mt-1 w-full bg-[#0F0F0F] border border-white/10 max-h-72 overflow-y-auto" data-testid="filter-yob-panel">
              <div className="p-2 border-b border-white/5 flex justify-between items-center">
                <span className="text-[10px] uppercase tracking-widest text-white/40">Year of Birth</span>
                <button onClick={() => setYobFilter(new Set())} className="text-[10px] uppercase tracking-widest text-[#D4AF37] hover:underline" data-testid="filter-yob-clear">Clear</button>
              </div>
              {yobOptions.length === 0 && <p className="text-white/40 text-xs p-3">No DOBs on file yet.</p>}
              {yobOptions.map((y) => (
                <label key={y} className="flex items-center gap-3 px-3 py-2 hover:bg-[#D4AF37]/10 cursor-pointer border-b border-white/5 last:border-0" data-testid={`filter-yob-option-${y}`}>
                  <input type="checkbox" checked={yobFilter.has(y)} onChange={() => toggleYob(y)} className="accent-[#D4AF37]" />
                  <span className="text-sm">{y}</span>
                </label>
              ))}
            </div>
          )}
        </div>
      </div>

      {bulkMode && (
        <form onSubmit={applyBulk} className="border border-[#D4AF37]/40 bg-[#D4AF37]/5 p-5 mb-6" data-testid="bulk-panel">
          <p className="text-xs tracking-[0.25em] uppercase text-[#D4AF37] font-semibold mb-4">Bulk Fee Update — {selected.size} selected</p>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <input type="number" min="0" placeholder="New group fee ₹" value={bulk.group_fee} onChange={(e) => setBulk({ ...bulk, group_fee: e.target.value })} className={inputCls} data-testid="bulk-group" />
            <input type="number" min="0" placeholder="Transport ₹ (optional)" value={bulk.transport_fee} onChange={(e) => setBulk({ ...bulk, transport_fee: e.target.value })} className={inputCls} data-testid="bulk-transport" />
            <input type="number" min="0" placeholder="PT ₹ (optional)" value={bulk.pt_fee} onChange={(e) => setBulk({ ...bulk, pt_fee: e.target.value })} className={inputCls} data-testid="bulk-pt" />
            <input required type="month" value={bulk.effective_from} onChange={(e) => setBulk({ ...bulk, effective_from: e.target.value })} className={inputCls} data-testid="bulk-from" />
            <button type="submit" className="bg-[#D4AF37] text-black font-bold uppercase tracking-widest text-xs px-5 py-2.5 rounded-sm hover:bg-[#F3D059] transition-colors duration-200" data-testid="bulk-apply">Apply</button>
          </div>
          <p className="text-white/40 text-xs mt-3">Each selected player's current plan ends the month before; the new plan starts in the chosen month. Empty fields keep the player's existing amount. Unselected players are untouched.</p>
        </form>
      )}

      {showAdd && (
        <div className="border border-[#D4AF37]/40 bg-[#0A0A0A] p-6 mb-8" data-testid="add-player-panel">
          <h2 className="font-heading text-lg font-bold mb-4">New Player</h2>
          <PlayerForm initial={emptyPlayer} onSave={addPlayer} onClose={() => setShowAdd(false)} saveLabel="Save Player" showPlan />
        </div>
      )}

      <div className="border border-white/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-widest text-white/40 border-b border-white/10">
              {bulkMode && (
                <th className="p-4 w-10">
                  <input type="checkbox" checked={filtered.length > 0 && selected.size === filtered.length} onChange={toggleAll} className="accent-[#D4AF37]" data-testid="bulk-select-all" />
                </th>
              )}
              <th className="p-4">ID</th>
              <th className="p-4">Name</th>
              <th className="p-4 hidden sm:table-cell">YOB</th>
              <th className="p-4 hidden md:table-cell">Batch</th>
              <th className="p-4 hidden lg:table-cell">Current Plan</th>
              <th className="p-4">Status</th>
              <th className="p-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {!playersLoaded && filtered.length === 0 && (
              <>
                {[0,1,2,3,4,5].map((i) => (
                  <tr key={`sk-${i}`} className="border-b border-white/5" data-testid={`players-loading-row-${i}`}>
                    {bulkMode && <td className="p-4"><div className="h-4 w-4 bg-white/10 animate-pulse" /></td>}
                    <td className="p-4"><div className="h-4 w-20 bg-white/10 animate-pulse" /></td>
                    <td className="p-4"><div className="h-4 w-40 bg-white/10 animate-pulse" /></td>
                    <td className="p-4 hidden sm:table-cell"><div className="h-4 w-12 bg-white/10 animate-pulse" /></td>
                    <td className="p-4 hidden md:table-cell"><div className="h-4 w-32 bg-white/10 animate-pulse" /></td>
                    <td className="p-4 hidden lg:table-cell"><div className="h-4 w-48 bg-white/10 animate-pulse" /></td>
                    <td className="p-4"><div className="h-4 w-16 bg-white/10 animate-pulse" /></td>
                    <td className="p-4 text-right"><div className="h-4 w-20 bg-white/10 animate-pulse inline-block" /></td>
                  </tr>
                ))}
              </>
            )}
            {playersLoaded && filtered.length === 0 && (
              <tr>
                <td colSpan={bulkMode ? 8 : 7} className="p-6 text-center text-white/40 text-sm" data-testid="players-empty">
                  No players match the current filters.
                </td>
              </tr>
            )}
            {filtered.map((p) => (
              <tr key={p.player_id}
                onClick={() => bulkMode ? toggle(p.player_id) : setDetailId(p.player_id)}
                className={`border-b border-white/5 hover:bg-[#0F0F0F] cursor-pointer transition-colors duration-150 ${selected.has(p.player_id) ? "bg-[#D4AF37]/10" : ""}`}
                data-testid={`player-row-${p.player_id}`}>
                {bulkMode && (
                  <td className="p-4" onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" checked={selected.has(p.player_id)} onChange={() => toggle(p.player_id)} className="accent-[#D4AF37]" data-testid={`bulk-check-${p.player_id}`} />
                  </td>
                )}
                <td className="p-4 text-[#D4AF37] font-semibold">{p.player_id}</td>
                <td className="p-4 font-semibold">{p.name}{p.gender ? <span className="text-white/40 text-xs ml-2">{p.gender}</span> : null}</td>
                <td className="p-4 text-white/60 hidden sm:table-cell">{p.dob ? p.dob.slice(0, 4) : "—"}</td>
                <td className="p-4 text-white/60 hidden md:table-cell">{p.batch || "—"}</td>
                <td className="p-4 hidden lg:table-cell" data-testid={`plan-cell-${p.player_id}`}>{planSummary(p.current_plan)}</td>
                <td className="p-4">
                  <span className={`text-[10px] uppercase tracking-widest px-2 py-1 border ${p.status === "Active" ? "border-[#D4AF37]/40 text-[#D4AF37]" : "border-white/20 text-white/40"}`}>{p.status}</span>
                </td>
                <td className="p-4 text-right" onClick={(e) => e.stopPropagation()}>
                  <button onClick={() => setWelcomePlayer(p)}
                    className="inline-flex items-center gap-1.5 border border-[#D4AF37]/40 text-[#D4AF37] text-[10px] uppercase tracking-widest px-3 py-1.5 hover:bg-[#D4AF37] hover:text-black transition-colors duration-200"
                    data-testid={`player-welcome-${p.player_id}`}>
                    <MessageCircle size={11} /> Welcome
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {detailId && <PlayerDetail playerId={detailId} onClose={() => { setDetailId(null); load(); }} />}
      {welcomePlayer && <WelcomeModal player={welcomePlayer} onClose={() => { setWelcomePlayer(null); load(); }} />}
    </div>
  );
}
